/* eslint-disable max-len */
import moment from 'moment-timezone';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Modal, { Styles } from 'react-modal';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Button from '@mui/material/Button';

import { useWindowSize } from '@brands/hooks';
import { listAvailableProviders as listAvailableProvidersForDate } from '@brands/services/availability/listAvailableProviders';
import { CaseType, ICase, Status } from '@brands/services/cases/types/ICase';
import { listAvailableCareAdvisors } from '@brands/services/identity/listAvailableCareAdvisors';
import { listAvailableProvidersByDate } from '@brands/services/identity/listAvailableProvidersByDate';
import { UserRoleName } from '@brands/services/identity/types/UserProfile';
import { selectAuth } from '@brands/store/selectors/auth';
import { customStylesDashboard } from '@brands/Utils/customStyles';
import { displayErrorDetails } from '@brands/Utils/displayError';

import { useAppSelector } from '../../../../hooks/useReduxHook';
import styles from './styles.module.scss';

type Props = {
  open: boolean;
  onClose: () => void;
  thisCase: ICase;
  setCase: React.Dispatch<React.SetStateAction<ICase>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  rescheduleCase: (case_id: number, video_conference_date: string) => Promise<ICase | null>;
  providerId?: number;
};

const hourList = ['12', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11'];
const minuteList = ['00', '05', '10', '15', '20', '25', '30', '35', '40', '45', '50', '55'];
const meridiemList = ['AM', 'PM'];
const weekdayLabels = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const SLOT_STEP_MINUTES = 5;
const DEFAULT_SLOT_EXPANSION_MINUTES = 30;

const isSameDay = (a: Date | null, b: Date | null): boolean => {
  if (!a || !b) return false;

  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
};

const getMonthGrid = (monthDate: Date): Date[] => {
  const firstDayOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const lastDayOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);

  const start = new Date(firstDayOfMonth);
  start.setDate(start.getDate() - start.getDay());

  const end = new Date(lastDayOfMonth);
  end.setDate(end.getDate() + (6 - end.getDay()));

  const days: Date[] = [];
  const cursor = new Date(start);

  while (cursor <= end) {
    days.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  return days;
};

const getPickerTimeFromSlot = (slot: string): { hour: string; minute: string; meridiem: string } => {
  const slotMoment = moment(slot, 'HH:mm');

  return {
    hour: slotMoment.format('hh'),
    minute: slotMoment.format('mm'),
    meridiem: slotMoment.format('A'),
  };
};

const getSlotDateTime = (dateKey: string, slot: string, timezone: string): moment.Moment =>
  moment.tz(`${dateKey} ${slot}`, 'YYYY-MM-DD HH:mm', timezone);

const roundUpToStep = (value: moment.Moment, stepMinutes: number): moment.Moment => {
  const rounded = value.clone().seconds(0).milliseconds(0);
  const minuteRemainder = rounded.minute() % stepMinutes;
  const shouldRoundUp = minuteRemainder > 0 || value.seconds() > 0 || value.milliseconds() > 0;

  if (shouldRoundUp) {
    rounded.add(minuteRemainder === 0 ? stepMinutes : stepMinutes - minuteRemainder, 'minutes');
  }

  return rounded;
};

const parseAvailabilityTime = (value: string, fallbackDateKey: string, timezone: string): moment.Moment | null => {
  const isoMoment = moment(value, moment.ISO_8601, true);

  if (isoMoment.isValid()) {
    return isoMoment.tz(timezone);
  }

  const timeMoment = moment.tz(`${fallbackDateKey} ${value}`, 'YYYY-MM-DD HH:mm', timezone);

  return timeMoment.isValid() ? timeMoment : null;
};

const addExpandedSlots = (slots: string[], start: moment.Moment, end: moment.Moment, timezone: string): void => {
  const cursor = roundUpToStep(start, SLOT_STEP_MINUTES);
  const localDateKey = start.clone().tz(timezone).format('YYYY-MM-DD');

  while (cursor.isBefore(end)) {
    if (cursor.clone().tz(timezone).format('YYYY-MM-DD') !== localDateKey) break;

    slots.push(cursor.toISOString());
    cursor.add(SLOT_STEP_MINUTES, 'minutes');
  }
};

const normalizeSlotsByDate = (slots: string[], fallbackDateKey: string, timezone: string): Record<string, string[]> => {
  const slotsByDate: Record<string, string[]> = {};

  slots.forEach((slot) => {
    const isoSlotMoment = moment(slot, moment.ISO_8601, true);
    const slotDateKey = isoSlotMoment.isValid() ? isoSlotMoment.tz(timezone).format('YYYY-MM-DD') : fallbackDateKey;
    const slotTime = isoSlotMoment.isValid() ? isoSlotMoment.tz(timezone).format('HH:mm') : slot;

    slotsByDate[slotDateKey] = [...(slotsByDate[slotDateKey] ?? []), slotTime];
  });

  return Object.fromEntries(
    Object.entries(slotsByDate).map(([dateKey, dateSlots]) => [dateKey, Array.from(new Set(dateSlots)).sort()]),
  );
};

const ChangeConsultDateTimeModal = ({
  open,
  onClose,
  thisCase,
  setCase,
  setLoading,
  rescheduleCase,
  providerId,
}: Props): JSX.Element => {
  const { width } = useWindowSize();
  const { userInfo } = useAppSelector(selectAuth);
  const isMobile = useMemo(() => width < 768, [width]);

  const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const initialLocalMoment = useMemo(
    () => roundUpToStep(moment().tz(browserTimeZone), SLOT_STEP_MINUTES),
    [browserTimeZone],
  );

  const [selectedDate, setSelectedDate] = useState<Date | null>(initialLocalMoment.toDate());
  const [visibleMonth, setVisibleMonth] = useState<Date>(
    new Date(initialLocalMoment.year(), initialLocalMoment.month(), 1),
  );
  const [selectedHour, setSelectedHour] = useState<string>(initialLocalMoment.format('hh'));
  const [selectedMinute, setSelectedMinute] = useState<string>(initialLocalMoment.format('mm'));
  const [selectedMeridiem, setSelectedMeridiem] = useState<string>(initialLocalMoment.format('A'));
  const [displayError, setDisplayError] = useState(false);
  const [providerUnavailableError, setProviderUnavailableError] = useState(false);
  const [availableSlotsByDate, setAvailableSlotsByDate] = useState<Record<string, string[]>>({});
  const [availableDatesByDate, setAvailableDatesByDate] = useState<Record<string, boolean>>({});
  const [availabilityLoaded, setAvailabilityLoaded] = useState(false);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const availabilityRequestIdRef = useRef(0);
  const slotsRequestIdRef = useRef(0);
  const userRoleName = userInfo?.role?.name;

  const fetchSlotsForDate = useCallback(
    async (dateKey: string): Promise<void> => {
      if (!providerId) return;

      const requestId = slotsRequestIdRef.current + 1;
      slotsRequestIdRef.current = requestId;

      const requestParams = {
        date: dateKey,
        tz: browserTimeZone,
        limit: 1,
        synchronous: true,
        case_id: typeof thisCase.id === 'number' ? thisCase.id : undefined,
      };
      let response: Awaited<ReturnType<typeof listAvailableCareAdvisors | typeof listAvailableProvidersForDate>>;
      try {
        response =
          thisCase.type === CaseType.oral_health_coaching
            ? await listAvailableCareAdvisors(
                'care_advisors',
                {
                  ...requestParams,
                  care_advisor_id: providerId,
                },
                userRoleName,
              )
            : await listAvailableProvidersForDate(
                {
                  ...requestParams,
                  provider_id: providerId,
                },
                userRoleName,
              );
      } catch (error) {
        if (requestId === slotsRequestIdRef.current) {
          displayErrorDetails(error);
        }
        return;
      }

      if (requestId !== slotsRequestIdRef.current) return;

      const providerAvailabilities = Array.isArray(response) ? response : response.nodes;
      const providerAvailability = providerAvailabilities[0];

      const allSlots: string[] = [];
      if (providerAvailability) {
        const rawIntervals = [
          ...(providerAvailability.sync_time_intervals || []),
          ...(providerAvailability.async_time_intervals || []),
        ];

        rawIntervals.forEach((interval) => {
          const start = parseAvailabilityTime(interval.start_time, dateKey, browserTimeZone);
          const end = parseAvailabilityTime(interval.end_time, dateKey, browserTimeZone);

          if (start && end && end.isAfter(start)) {
            addExpandedSlots(allSlots, start, end, browserTimeZone);
          }
        });

        if (allSlots.length === 0) {
          const rawSlots = [
            ...(providerAvailability.sync_time_slots || []),
            ...(providerAvailability.async_time_slots || []),
          ];

          rawSlots.forEach((slot) => {
            const slotMoment = parseAvailabilityTime(slot, dateKey, browserTimeZone);

            if (slotMoment) {
              addExpandedSlots(
                allSlots,
                slotMoment,
                slotMoment.clone().add(DEFAULT_SLOT_EXPANSION_MINUTES, 'minutes'),
                browserTimeZone,
              );
            } else {
              allSlots.push(slot);
            }
          });
        }
      }

      const normalizedSlotsByDate = normalizeSlotsByDate(allSlots, dateKey, browserTimeZone);

      setAvailableSlotsByDate((prev) => ({
        ...prev,
        ...normalizedSlotsByDate,
      }));
      setAvailableDatesByDate((prev) => ({
        ...prev,
        ...Object.fromEntries(Object.keys(normalizedSlotsByDate).map((slotDateKey) => [slotDateKey, true])),
      }));
    },
    [providerId, browserTimeZone, thisCase.id, thisCase.type, userRoleName],
  );

  const fetchAvailability = useCallback(
    async (monthDate: Date): Promise<void> => {
      const requestId = availabilityRequestIdRef.current + 1;
      availabilityRequestIdRef.current = requestId;
      const monthGrid = getMonthGrid(monthDate);
      const gridDateKeys = new Set(monthGrid.map((day) => moment(day).format('YYYY-MM-DD')));
      const todayLocal = moment().tz(browserTimeZone).startOf('day');
      const requestStartDay = monthGrid.find((day) => !moment(day).isBefore(todayLocal, 'day'));

      setAvailableSlotsByDate({});
      setAvailableDatesByDate({});
      setAvailabilityLoaded(false);
      slotsRequestIdRef.current += 1;

      if (!providerId) {
        setAvailabilityLoaded(true);
        setLoadingAvailability(false);
        return;
      }

      if (!requestStartDay) {
        setAvailabilityLoaded(true);
        setLoadingAvailability(false);
        return;
      }

      setLoadingAvailability(true);

      try {
        const requestEndDay = monthGrid[monthGrid.length - 1];
        const period = moment(requestEndDay).diff(moment(requestStartDay), 'days') + 1;
        const isOralHealthCoaching = thisCase.type === CaseType.oral_health_coaching;
        const response = await listAvailableProvidersByDate(
          isOralHealthCoaching ? UserRoleName.CareAdvisor : UserRoleName.Provider,
          {
            start: moment(requestStartDay).format('YYYY-MM-DD'),
            tz: browserTimeZone,
            period,
            ...(isOralHealthCoaching ? { care_advisor_id: providerId } : { provider_id: providerId }),
            synchronous: true,
            case_id: typeof thisCase.id === 'number' ? thisCase.id : undefined,
          },
        );

        if (requestId !== availabilityRequestIdRef.current) return;

        const dateMap: Record<string, boolean> = {};

        response.nodes.forEach((dateKey) => {
          if (gridDateKeys.has(dateKey)) {
            dateMap[dateKey] = true;
          }
        });

        setAvailableDatesByDate(dateMap);
        setAvailabilityLoaded(true);

        const firstAvailableDateKey = response.nodes.find((dateKey) => gridDateKeys.has(dateKey));
        if (firstAvailableDateKey) {
          setSelectedDate(moment.tz(firstAvailableDateKey, 'YYYY-MM-DD', browserTimeZone).toDate());
          fetchSlotsForDate(firstAvailableDateKey);
        }
      } catch (error) {
        if (requestId !== availabilityRequestIdRef.current) return;
        setAvailabilityLoaded(false);
        displayErrorDetails(error);
      } finally {
        if (requestId === availabilityRequestIdRef.current) {
          setLoadingAvailability(false);
        }
      }
    },
    [providerId, browserTimeZone, thisCase.id, thisCase.type, fetchSlotsForDate],
  );

  useEffect(() => {
    if (!open) return;

    const localMoment = roundUpToStep(moment().tz(browserTimeZone), SLOT_STEP_MINUTES);
    const newMonth = new Date(localMoment.year(), localMoment.month(), 1);

    setSelectedDate(localMoment.toDate());
    setVisibleMonth(newMonth);
    setSelectedHour(localMoment.format('hh'));
    setSelectedMinute(localMoment.format('mm'));
    setSelectedMeridiem(localMoment.format('A'));
    setDisplayError(false);
    setProviderUnavailableError(false);
    setAvailableSlotsByDate({});
    setAvailableDatesByDate({});
    setAvailabilityLoaded(false);
    fetchAvailability(newMonth);
  }, [open, browserTimeZone, fetchAvailability]);

  const calendarDays = useMemo(() => getMonthGrid(visibleMonth), [visibleMonth]);

  const today = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);

  const consultTime = useMemo(() => {
    if (!selectedDate || !selectedHour || !selectedMinute || !selectedMeridiem) {
      return null;
    }

    let hour24 = Number(selectedHour) % 12;

    if (selectedMeridiem === 'PM') {
      hour24 += 12;
    }

    const localMoment = moment.tz(
      {
        year: selectedDate.getFullYear(),
        month: selectedDate.getMonth(),
        date: selectedDate.getDate(),
        hour: hour24,
        minute: Number(selectedMinute),
        second: 0,
      },
      browserTimeZone,
    );

    return localMoment.utc().toDate();
  }, [selectedDate, selectedHour, selectedMinute, selectedMeridiem, browserTimeZone]);

  const isPastConsultTime = (time: Date | null): boolean => {
    if (!time) return false;
    return time.getTime() < Date.now();
  };

  const selectedDateKey = useMemo(() => {
    if (!selectedDate) return null;

    return moment(selectedDate).format('YYYY-MM-DD');
  }, [selectedDate]);

  const getAvailableFutureSlots = useCallback(
    (dateKey: string): string[] => {
      const slots = availableSlotsByDate[dateKey] ?? [];

      return slots.filter((slot) => getSlotDateTime(dateKey, slot, browserTimeZone).valueOf() >= Date.now());
    },
    [availableSlotsByDate, browserTimeZone],
  );

  const selectedDateAvailableSlots = useMemo(() => {
    if (!providerId || !availabilityLoaded || !selectedDateKey) return [];

    return getAvailableFutureSlots(selectedDateKey);
  }, [providerId, availabilityLoaded, selectedDateKey, getAvailableFutureSlots]);

  const isSelectedTimeAvailable = useMemo(() => {
    if (!providerId) return false;
    if (!availabilityLoaded) return true;
    if (!consultTime || !selectedDateAvailableSlots) return false;

    const selectedTimeStr = moment(consultTime).tz(browserTimeZone).format('HH:mm');

    return selectedDateAvailableSlots.includes(selectedTimeStr);
  }, [providerId, availabilityLoaded, consultTime, selectedDateAvailableSlots, browserTimeZone]);

  const isMeridiemDisabled = useCallback(
    (meridiem: string): boolean => {
      if (!providerId) return true;
      if (!availabilityLoaded || !selectedDateAvailableSlots) return true;

      return !selectedDateAvailableSlots.some((slot) => getPickerTimeFromSlot(slot).meridiem === meridiem);
    },
    [providerId, availabilityLoaded, selectedDateAvailableSlots],
  );

  const isHourDisabled = useCallback(
    (hour: string): boolean => {
      if (!providerId) return true;
      if (!availabilityLoaded || !selectedDateAvailableSlots) return true;

      return !selectedDateAvailableSlots.some((slot) => {
        const slotTime = getPickerTimeFromSlot(slot);
        return slotTime.hour === hour && slotTime.meridiem === selectedMeridiem;
      });
    },
    [providerId, availabilityLoaded, selectedDateAvailableSlots, selectedMeridiem],
  );

  const isMinuteDisabled = useCallback(
    (minute: string): boolean => {
      if (!providerId) return true;
      if (!availabilityLoaded || !selectedDateAvailableSlots) return true;

      return !selectedDateAvailableSlots.some((slot) => {
        const slotTime = getPickerTimeFromSlot(slot);
        return slotTime.hour === selectedHour && slotTime.minute === minute && slotTime.meridiem === selectedMeridiem;
      });
    },
    [providerId, availabilityLoaded, selectedDateAvailableSlots, selectedHour, selectedMeridiem],
  );

  const setSelectedTimeFromSlot = useCallback((slot: string): void => {
    const slotTime = getPickerTimeFromSlot(slot);
    setSelectedHour(slotTime.hour);
    setSelectedMinute(slotTime.minute);
    setSelectedMeridiem(slotTime.meridiem);
  }, []);

  const handleSelectHour = (hour: string): void => {
    if (!selectedDateAvailableSlots) {
      setSelectedHour(hour);
      return;
    }

    const firstMatchingSlot = selectedDateAvailableSlots.find((slot) => {
      const slotTime = getPickerTimeFromSlot(slot);
      return slotTime.hour === hour && slotTime.meridiem === selectedMeridiem;
    });

    if (firstMatchingSlot) {
      setSelectedTimeFromSlot(firstMatchingSlot);
    }
  };

  const handleSelectMeridiem = (meridiem: string): void => {
    if (!selectedDateAvailableSlots) {
      setSelectedMeridiem(meridiem);
      return;
    }

    const firstMatchingSlot = selectedDateAvailableSlots.find(
      (slot) => getPickerTimeFromSlot(slot).meridiem === meridiem,
    );

    if (firstMatchingSlot) {
      setSelectedTimeFromSlot(firstMatchingSlot);
    }
  };

  useEffect(() => {
    if (!providerId || !availabilityLoaded || !selectedDateAvailableSlots?.length || isSelectedTimeAvailable) return;

    setSelectedTimeFromSlot(selectedDateAvailableSlots[0]);
  }, [providerId, availabilityLoaded, selectedDateAvailableSlots, isSelectedTimeAvailable, setSelectedTimeFromSlot]);

  // Resolves to whether the reschedule succeeded; errors are surfaced here so the caller doesn't toast twice.
  const changeCaseDue = async (formattedDate: string): Promise<boolean> => {
    if (typeof thisCase.id === 'number' && formattedDate) {
      try {
        setLoading(true);
        const updatedCase = await rescheduleCase(thisCase.id, formattedDate);

        if (updatedCase) {
          setCase((prev) => ({
            ...prev,
            video_conference_date: updatedCase.video_conference_date,
            due_date: updatedCase.due_date,
            status: updatedCase.status,
            updated_at: updatedCase.updated_at,
          }));
        }
      } catch (error: unknown) {
        displayErrorDetails(error);
        return false;
      } finally {
        setLoading(false);
      }
    }
    return true;
  };

  // Sync guard against double-fire on the Ok button (same shape as the
  // EnforceAssignCAModal fix in DO-4019). The button's disabled prop only
  // gates on form state, not on "currently submitting", so a fast double-
  // click can fire handleApply twice and trigger spurious error toasts
  // from the second rescheduleCase call.
  const isSubmittingRef = useRef(false);

  const handleApply = async (): Promise<void> => {
    if (thisCase.status === Status.Canceled) {
      onClose();
      return;
    }

    if (!consultTime) return;

    if (isPastConsultTime(consultTime)) {
      setDisplayError(true);
      return;
    }

    if (!isSelectedTimeAvailable) {
      setProviderUnavailableError(true);
      return;
    }

    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    try {
      const formattedDate = consultTime.toISOString().replace(/\.[0-9]{3}Z$/, 'Z');
      if (await changeCaseDue(formattedDate)) {
        onClose();
      }
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const handlePrevMonth = (): void => {
    const newMonth = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() - 1, 1);
    setVisibleMonth(newMonth);
    fetchAvailability(newMonth);
  };

  const handleNextMonth = (): void => {
    const newMonth = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 1);
    setVisibleMonth(newMonth);
    fetchAvailability(newMonth);
  };

  const handleSelectDate = (day: Date): void => {
    const dayOnly = new Date(day.getFullYear(), day.getMonth(), day.getDate());

    if (dayOnly < today) return;

    setSelectedDate(dayOnly);
    const dateKey = moment(dayOnly).format('YYYY-MM-DD');
    const slots = getAvailableFutureSlots(dateKey);
    const firstSlot = slots[0];

    if (firstSlot) {
      setSelectedTimeFromSlot(firstSlot);
    } else {
      fetchSlotsForDate(dateKey);
    }

    setDisplayError(false);
    setProviderUnavailableError(false);
  };

  const modalStyles: Styles = isMobile
    ? {
        ...customStylesDashboard,
        content: {
          ...customStylesDashboard.content,
          width: '100vw',
          height: '100vh',
          borderRadius: 0,
          padding: '90px 16px 32px 16px',
        },
        overlay: {
          ...(customStylesDashboard.overlay || {}),
          overflowY: 'auto',
        },
      }
    : {
        ...customStylesDashboard,
        content: {
          ...customStylesDashboard.content,
          width: '640px',
          maxWidth: '95vw',
          padding: '0',
          borderRadius: '8px',
          overflow: 'hidden',
        },
      };

  return (
    <Modal isOpen={open} onRequestClose={onClose} ariaHideApp={false} style={modalStyles}>
      <div className={styles.changeConsultDateTimeModal}>
        <div className={styles.header}>
          <span className={styles.title}>Change consultation date & time</span>
          <CloseIcon className={styles.closeIcon} onClick={onClose} />
        </div>

        <div className={styles.divider} />

        <div className={styles.content}>
          <div className={styles.schedulerLayout}>
            <div className={styles.calendarPanel}>
              <div className={styles.calendarHeader}>
                <div className={styles.calendarMonthLabel}>
                  {visibleMonth.toLocaleString('default', {
                    month: 'long',
                    year: 'numeric',
                  })}
                </div>

                <div className={styles.calendarHeaderActions}>
                  <button type="button" className={styles.calendarArrowBtn} onClick={handlePrevMonth}>
                    <ChevronLeftIcon />
                  </button>
                  <button type="button" className={styles.calendarArrowBtn} onClick={handleNextMonth}>
                    <ChevronRightIcon />
                  </button>
                </div>
              </div>

              <div className={styles.weekdaysRow}>
                {weekdayLabels.map((label) => (
                  <div key={label} className={styles.weekdayCell}>
                    {label}
                  </div>
                ))}
              </div>

              <div className={styles.daysGrid}>
                {calendarDays.map((day, index) => {
                  const isCurrentMonth = day.getMonth() === visibleMonth.getMonth();
                  const dayOnly = new Date(day.getFullYear(), day.getMonth(), day.getDate());
                  const isPastDay = dayOnly < today;
                  const dateKey = moment(dayOnly).format('YYYY-MM-DD');
                  const hasProviderSlots = availabilityLoaded && availableDatesByDate[dateKey];
                  const isDisabled = !isCurrentMonth || isPastDay || !hasProviderSlots;
                  const isSelected = isSameDay(dayOnly, selectedDate);
                  const isToday = isSameDay(dayOnly, today);

                  return (
                    <button
                      key={`${day.toISOString()}-${index}`}
                      type="button"
                      className={[
                        styles.dayCell,
                        !isCurrentMonth ? styles.dayCellMuted : '',
                        isDisabled ? styles.dayCellDisabled : '',
                        isSelected ? styles.dayCellSelected : '',
                        isToday ? styles.dayCellToday : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => handleSelectDate(day)}
                      disabled={isDisabled}
                    >
                      {day.getDate()}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className={styles.timePanel}>
              <div className={styles.timeColumn}>
                {hourList.map((hour) => {
                  const isDisabled = isHourDisabled(hour);

                  return (
                    <button
                      key={hour}
                      type="button"
                      className={`${styles.timeOption} ${selectedHour === hour ? styles.timeOptionSelected : ''}`}
                      onClick={() => {
                        if (!isDisabled) handleSelectHour(hour);
                      }}
                      disabled={isDisabled}
                    >
                      {hour}
                    </button>
                  );
                })}
              </div>

              <div className={styles.timeColumn}>
                {minuteList.map((minute) => {
                  const isDisabled = isMinuteDisabled(minute);

                  return (
                    <button
                      key={minute}
                      type="button"
                      className={`${styles.timeOption} ${selectedMinute === minute ? styles.timeOptionSelected : ''}`}
                      onClick={() => {
                        if (!isDisabled) setSelectedMinute(minute);
                      }}
                      disabled={isDisabled}
                    >
                      {minute}
                    </button>
                  );
                })}
              </div>

              <div className={styles.timeColumn}>
                {meridiemList.map((meridiem) => {
                  const isDisabled = isMeridiemDisabled(meridiem);

                  return (
                    <button
                      key={meridiem}
                      type="button"
                      className={`${styles.timeOption} ${
                        selectedMeridiem === meridiem ? styles.timeOptionSelected : ''
                      }`}
                      onClick={() => {
                        if (!isDisabled) handleSelectMeridiem(meridiem);
                      }}
                      disabled={isDisabled}
                    >
                      {meridiem}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {(displayError || isPastConsultTime(consultTime)) && (
            <div className={styles.warning}>You are about to schedule a past date/time. Please try again.</div>
          )}
          {providerUnavailableError && (
            <div className={styles.warning}>
              This provider is already booked or unavailable at the selected time. Please choose a different time.
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <Button
            variant="text"
            className={styles.okBtn}
            onClick={handleApply}
            disabled={
              !selectedDate ||
              !selectedHour ||
              !selectedMinute ||
              !selectedMeridiem ||
              loadingAvailability ||
              !isSelectedTimeAvailable
            }
          >
            Ok
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ChangeConsultDateTimeModal;
